// frontend/src/portal/pages/OpsTenantMembers.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchSuperAdminTenantMembers, patchSuperAdminTenantMember, resendSuperAdminTenantInvitation, revokeSuperAdminTenantInvitation } from "../../api.js";
import { usePortalAuth } from "../components/RequireClient.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";

// ============================================================================
// FORMATTERS
// ============================================================================

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formatRole(role) {
  if (!role) return "—";
  switch (role) {
    case "owner":
      return "Owner";
    case "admin":
      return "Admin";
    case "reviewer":
      return "Reviewer";
    case "staff":
      return "Staff";
    case "client_user":
      return "Client user";
    case "super_admin":
      return "Super admin";
    default:
      return role;
  }
}

function formatStatus(status) {
  if (!status) return "—";
  switch (status) {
    case "active":
      return "Active";
    case "inactive":
      return "Inactive";
    case "suspended":
      return "Suspended";
    default:
      return status;
  }
}

function formatInvitationStatus(status) {
  if (!status) return "—";
  switch (status) {
    case "pending":
      return "Pending";
    case "accepted":
      return "Accepted";
    case "expired":
      return "Expired";
    case "revoked":
      return "Revoked";
    default:
      return status;
  }
}

// ============================================================================
// CONFIRMATION DIALOG
// ============================================================================

function ConfirmDialog({ isOpen, title, message, confirmText, cancelText, onConfirm, onCancel, variant = "danger" }) {
  if (!isOpen) return null;

  return (
    <div className="portal-modal-overlay" onClick={onCancel}>
      <div className="portal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="portal-modal__header">
          <h2>{title}</h2>
        </div>
        <div className="portal-modal__body">
          <p>{message}</p>
        </div>
        <div className="portal-modal__footer">
          <button className="portal-button portal-button--ghost" onClick={onCancel}>
            {cancelText || "Cancel"}
          </button>
          <button className={`portal-button portal-button--${variant}`} onClick={onConfirm}>
            {confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ROLE CHANGE DIALOG
// ============================================================================

function RoleChangeDialog({ isOpen, currentRole, memberName, onConfirm, onCancel }) {
  const [selectedRole, setSelectedRole] = useState(currentRole);

  useEffect(() => {
    if (isOpen) {
      setSelectedRole(currentRole);
    }
  }, [isOpen, currentRole]);

  if (!isOpen) return null;

  const roles = [
    { value: "owner", label: "Owner" },
    { value: "admin", label: "Admin" },
    { value: "reviewer", label: "Reviewer" },
    { value: "staff", label: "Staff" },
    { value: "client_user", label: "Client user" },
  ];

  return (
    <div className="portal-modal-overlay" onClick={onCancel}>
      <div className="portal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="portal-modal__header">
          <h2>Change role</h2>
        </div>
        <div className="portal-modal__body">
          <p style={{ marginBottom: 16 }}>
            Change role for <strong>{memberName}</strong>
          </p>
          <div className="portal-form-field">
            <label className="portal-label">New role</label>
            <select className="portal-select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
              {roles.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="portal-modal__footer">
          <button className="portal-button portal-button--ghost" onClick={onCancel}>
            Cancel
          </button>
          <button className="portal-button portal-button--primary" onClick={() => onConfirm(selectedRole)} disabled={selectedRole === currentRole}>
            Change role
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MEMBER ROW COMPONENT
// ============================================================================

function MemberRow({ member, onAction }) {
  const [actionLoading, setActionLoading] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  const isMember = member.source === "member";
  const isInvitation = member.source === "invitation_only";
  const isPending = member.invitationStatus === "pending";

  const handleAction = async (action, data = {}) => {
    setActionLoading(action);
    setShowMenu(false);
    try {
      await onAction(member, action, data);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <tr>
      <td>
        <span className={isMember ? "portal-chip portal-chip--success" : "portal-chip"}>{isMember ? "Member" : "Invitation only"}</span>
      </td>
      <td>{member.name || "—"}</td>
      <td>{member.email}</td>
      <td>{formatRole(member.role)}</td>
      <td>
        <span className={`portal-chip ${member.status === "active" ? "portal-chip--success" : member.status === "suspended" ? "portal-chip--error" : ""}`}>
          {formatStatus(member.status)}
        </span>
      </td>
      <td>
        <span
          className={`portal-chip ${
            member.invitationStatus === "accepted"
              ? "portal-chip--success"
              : member.invitationStatus === "pending"
              ? "portal-chip--warning"
              : member.invitationStatus === "revoked"
              ? "portal-chip--error"
              : ""
          }`}>
          {formatInvitationStatus(member.invitationStatus)}
        </span>
      </td>
      <td>{formatDate(member.invitedAt)}</td>
      <td>{formatDate(member.acceptedAt)}</td>
      <td>
        <div className="portal-actions" style={{ position: "relative" }}>
          <button className="portal-button portal-button--ghost portal-button--sm" onClick={() => setShowMenu(!showMenu)} disabled={actionLoading !== null}>
            {actionLoading ? "Loading..." : "Actions ▾"}
          </button>

          {showMenu && (
            <>
              <div className="portal-actions-overlay" onClick={() => setShowMenu(false)} />
              <div className="portal-actions-menu">
                {/* Member actions */}
                {isMember && (
                  <>
                    {member.status === "active" && (
                      <button className="portal-actions-menu__item" onClick={() => handleAction("suspend")}>
                        Suspend member
                      </button>
                    )}
                    {member.status === "suspended" && (
                      <button className="portal-actions-menu__item" onClick={() => handleAction("activate")}>
                        Activate member
                      </button>
                    )}
                    <button className="portal-actions-menu__item" onClick={() => handleAction("change_role")}>
                      Change role
                    </button>
                    <div className="portal-actions-menu__divider" />
                    <button className="portal-actions-menu__item portal-actions-menu__item--danger" onClick={() => handleAction("remove")}>
                      Remove member
                    </button>
                  </>
                )}

                {/* Invitation actions */}
                {isInvitation && isPending && (
                  <>
                    <button className="portal-actions-menu__item" onClick={() => handleAction("resend")}>
                      Resend invitation
                    </button>
                    <button className="portal-actions-menu__item portal-actions-menu__item--danger" onClick={() => handleAction("revoke")}>
                      Revoke invitation
                    </button>
                  </>
                )}

                {/* No actions available */}
                {isInvitation && !isPending && <div className="portal-actions-menu__item portal-actions-menu__item--disabled">No actions available</div>}
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function OpsTenantMembers() {
  const { tenantId } = useParams();
  const { memberRole } = usePortalAuth();
  const isSuperAdmin = memberRole === "super_admin";

  const [tenant, setTenant] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "",
    variant: "danger",
    onConfirm: null,
  });

  // Role change dialog
  const [roleDialog, setRoleDialog] = useState({
    isOpen: false,
    member: null,
  });

  // Load members
  useEffect(() => {
    if (!tenantId || !isSuperAdmin) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchSuperAdminTenantMembers(tenantId);
        if (cancelled) return;
        setTenant(data.tenant || null);
        setMembers(Array.isArray(data.members) ? data.members : []);
      } catch (err) {
        if (cancelled) return;
        console.error("[OpsTenantMembers] Failed to load members", err);
        setError(err?.message || "Failed to load tenant members.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId, isSuperAdmin]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Seat usage calculation
  const seatUsageLabel = useMemo(() => {
    if (!tenant) return "—";

    const seats = tenant.seats != null && Number.isFinite(Number(tenant.seats)) ? Number(tenant.seats) : null;
    const activeMembers = members.filter((m) => m.source === "member").length;

    if (!seats) {
      return `${activeMembers} member${activeMembers === 1 ? "" : "s"}`;
    }
    return `${activeMembers} / ${seats} seats used`;
  }, [tenant, members]);

  // Handle member actions
  const handleMemberAction = async (member, action, data = {}) => {
    const isMember = member.source === "member";
    const isInvitation = member.source === "invitation_only";

    try {
      if (action === "suspend") {
        setConfirmDialog({
          isOpen: true,
          title: "Suspend member",
          message: `Are you sure you want to suspend ${member.name || member.email}? They will lose access to this tenant.`,
          confirmText: "Suspend",
          variant: "danger",
          onConfirm: async () => {
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            await patchSuperAdminTenantMember(tenantId, member.userId, "suspend");
            setSuccessMessage("Member suspended successfully");
            await reloadMembers();
          },
        });
      } else if (action === "activate") {
        await patchSuperAdminTenantMember(tenantId, member.userId, "activate");
        setSuccessMessage("Member activated successfully");
        await reloadMembers();
      } else if (action === "change_role") {
        setRoleDialog({
          isOpen: true,
          member,
        });
      } else if (action === "remove") {
        setConfirmDialog({
          isOpen: true,
          title: "Remove member",
          message: `Are you sure you want to permanently remove ${member.name || member.email} from this tenant? This action cannot be undone.`,
          confirmText: "Remove",
          variant: "danger",
          onConfirm: async () => {
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            await patchSuperAdminTenantMember(tenantId, member.userId, "remove");
            setSuccessMessage("Member removed successfully");
            await reloadMembers();
          },
        });
      } else if (action === "resend") {
        setConfirmDialog({
          isOpen: true,
          title: "Resend invitation",
          message: `Resend invitation to ${member.email}?`,
          confirmText: "Resend",
          variant: "primary",
          onConfirm: async () => {
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            // Need to get invitation ID from member data
            const invitationId = member.invitationId || member.id;
            await resendSuperAdminTenantInvitation(tenantId, invitationId);
            setSuccessMessage("Invitation resent successfully");
            await reloadMembers();
          },
        });
      } else if (action === "revoke") {
        setConfirmDialog({
          isOpen: true,
          title: "Revoke invitation",
          message: `Are you sure you want to revoke the invitation for ${member.email}?`,
          confirmText: "Revoke",
          variant: "danger",
          onConfirm: async () => {
            setConfirmDialog({ ...confirmDialog, isOpen: false });
            const invitationId = member.invitationId || member.id;
            await revokeSuperAdminTenantInvitation(tenantId, invitationId);
            setSuccessMessage("Invitation revoked successfully");
            await reloadMembers();
          },
        });
      }
    } catch (err) {
      console.error("[OpsTenantMembers] Action failed:", err);
      setError(err?.message || "Action failed");
    }
  };

  // Handle role change
  const handleRoleChange = async (newRole) => {
    const member = roleDialog.member;
    setRoleDialog({ isOpen: false, member: null });

    try {
      await patchSuperAdminTenantMember(tenantId, member.userId, "change_role", { role: newRole });
      setSuccessMessage(`Role changed to ${formatRole(newRole)} successfully`);
      await reloadMembers();
    } catch (err) {
      console.error("[OpsTenantMembers] Role change failed:", err);
      setError(err?.message || "Role change failed");
    }
  };

  // Reload members after action
  const reloadMembers = async () => {
    try {
      const data = await fetchSuperAdminTenantMembers(tenantId);
      setTenant(data.tenant || null);
      setMembers(Array.isArray(data.members) ? data.members : []);
    } catch (err) {
      console.error("[OpsTenantMembers] Failed to reload members", err);
    }
  };

  // Access control
  if (!isSuperAdmin) {
    return (
      <ErrorBoundary componentName="OpsTenantMembers">
        <section className="portal-section">
          <header className="portal-section__header">
            <h1>Tenant members</h1>
          </header>
          <div className="portal-card">
            <div className="portal-card__body">
              <p>You need super admin access to view this page.</p>
            </div>
          </div>
        </section>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary componentName="OpsTenantMembers">
      <>
        <section className="portal-section portal-section--wide">
          <header className="portal-section__header">
            <div>
              <h1>Tenant members</h1>
              {tenant ? (
                <p className="portal-section__subtitle">
                  {tenant.name} {tenant.slug ? <span className="portal-chip">slug: {tenant.slug}</span> : null}{" "}
                  <span className={`portal-chip portal-chip--status-${tenant.status || "default"}`}>{tenant.status || "unknown"}</span>
                </p>
              ) : (
                <p className="portal-section__subtitle">Loading tenant details…</p>
              )}
            </div>
            <div>
              <Link to="/portal/ops/tenants" className="portal-button portal-button--ghost">
                ← Back to tenants
              </Link>
            </div>
          </header>

          {/* Success message */}
          {successMessage && (
            <div className="portal-alert portal-alert--success" style={{ marginBottom: 24 }}>
              {successMessage}
            </div>
          )}

          {/* Overview section */}
          <section className="portal-card" style={{ marginBottom: 24 }}>
            <div className="portal-card__header">
              <h2>Overview</h2>
            </div>
            <div className="portal-card__body portal-card__body--grid">
              <div className="portal-stat">
                <div className="portal-stat__label">Seats</div>
                <div className="portal-stat__value">{seatUsageLabel}</div>
              </div>
              <div className="portal-stat">
                <div className="portal-stat__label">Clerk organization</div>
                <div className="portal-stat__value">
                  {tenant?.clerkOrgId || tenant?.clerk_org_id ? <code className="portal-code">{tenant.clerkOrgId || tenant.clerk_org_id}</code> : "Not linked"}
                </div>
              </div>
            </div>
          </section>

          {/* Members table */}
          <section className="portal-card">
            <div className="portal-card__header">
              <h2>Members & invitations</h2>
            </div>
            <div className="portal-card__body">
              {loading && <p>Loading members…</p>}
              {error && !loading && (
                <div className="portal-alert portal-alert--error" style={{ marginBottom: 8 }}>
                  {error}
                </div>
              )}
              {!loading && !error && members.length === 0 && <p className="portal-empty-state">No members or invitations found.</p>}

              {!loading && !error && members.length > 0 && (
                <div className="portal-table-wrapper">
                  <table className="portal-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Invitation</th>
                        <th>Invited</th>
                        <th>Accepted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map((m, idx) => (
                        <MemberRow key={`${m.source}-${m.email}-${m.userId || idx}`} member={m} onAction={handleMemberAction} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </section>

        {/* Confirmation dialog */}
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          variant={confirmDialog.variant}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        />

        {/* Role change dialog */}
        <RoleChangeDialog
          isOpen={roleDialog.isOpen}
          currentRole={roleDialog.member?.role}
          memberName={roleDialog.member?.name || roleDialog.member?.email}
          onConfirm={handleRoleChange}
          onCancel={() => setRoleDialog({ isOpen: false, member: null })}
        />
      </>
    </ErrorBoundary>
  );
}
